import * as React from "react";

import {
  type ColumnFiltersAction,
  DeleteStageAction,
  SetFilterAction,
} from "./actions";
import type { FilterStage } from "./state";

/**
 * Provides memoized dispatch helpers for column filter stage mutations.
 *
 * Used by both FilterBar (pill popovers) and TableHeader (column header popovers)
 * to avoid duplicating the same dispatch wiring.
 */
export function useColumnFilterActions(
  columnId: string,
  dispatch: React.Dispatch<ColumnFiltersAction>,
) {
  const onStageChange = React.useCallback(
    (stageIndex: number, stage: FilterStage) => {
      dispatch(
        new SetFilterAction({
          columnId,
          stageIndex,
          value: stage.value,
          condition: stage.condition,
          ...(stage.value2 !== undefined && { value2: stage.value2 }),
        }),
      );
    },
    [columnId, dispatch],
  );

  const onStageAdd = React.useCallback(
    (stage: FilterStage) => {
      dispatch(
        new SetFilterAction({
          columnId,
          value: stage.value,
          condition: stage.condition,
          ...(stage.value2 !== undefined && { value2: stage.value2 }),
        }),
      );
    },
    [columnId, dispatch],
  );

  const onStageDelete = React.useCallback(
    (stageIndex: number) => {
      dispatch(new DeleteStageAction({ columnId, stageIndex }));
    },
    [columnId, dispatch],
  );

  return { onStageChange, onStageAdd, onStageDelete };
}
